import { useCallback, useRef } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { ChatHttpError, resumeChat, type TurnContext } from "../services/api";
import type { Channel } from "../../shared/host";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { ApprovalRequest, ChatMessage } from "../types";
import type { CapabilitySpan } from "../../telemetry";
import {
  createTurnEventHandler,
  createTurnState,
  dispatchPendingToolCall,
  type TurnDeps,
} from "./turnEvents";

/**
 * Approve/Deny flow for an in-chat approval card (chat v2, AIVA-5929).
 *
 * A HITL-gated action paused the run server-side; the user's decision is sent
 * via POST /chat/resume with kind="approval" and the continuation streams into
 * a NEW assistant message driven by a fresh per-turn event handler.
 *
 * The decision is stamped optimistically (disabling the card's buttons). On a
 * network-level failure — the fetch rejected, or the backend was unreachable /
 * 5xx — the stamp is rolled back so the user can retry; on a 4xx the decision
 * reached the backend, so the stamp stays.
 */

export type ApprovalDecisionHandler = (
  messageId: string,
  approval: ApprovalRequest,
  decision: "approve" | "reject",
) => Promise<void>;

export interface UseApprovalDecisionArgs {
  apiToken: string | null;
  host: Channel | null;
  threadIdRef: MutableRefObject<string | null>;
  refresh?: RefreshTokenFn;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setStreaming: (streaming: boolean) => void;
  nextMsgId: () => string;
  /** Builds the hook-level TurnDeps for the continuation's event handler. */
  makeTurnDeps: (assistantMsgId: string, turnTimestamp: number) => TurnDeps;
  /** Registers this stream's AbortController with useChat's live-stream set
   *  (so panel unmount / clear-chat cancels it too) and returns the disposer
   *  to call when the stream settles. A shared single-slot ref would let this
   *  continuation clobber a concurrently live chat turn's controller. */
  trackAbort: (controller: AbortController) => () => void;
  /** Ids the user explicitly Stopped (shared with useChat). Lets an aborted
   *  continuation tell a user-initiated Stop apart from an unmount/new-chat
   *  abort, so the partial bubble is preserved with its `stopped` flag instead
   *  of being dropped — matching handleStreamError's wasUserStopped path. */
  userStoppedIdsRef: MutableRefObject<Set<string>>;
  /** Current client-owned resume context (open email / latest draft / search /
   *  document). Re-sent on resume so the paused tool rebuilds with its
   *  message-id / draft / search context. Kept client-side, never persisted
   *  server-side (AIVA-5925). Without it a HITL-gated attachment draft
   *  resumes with its tool unbound and silently no-ops. */
  getResumeContext: () => TurnContext;
  onCapabilityRunEnded?: () => void;
}

/** Network-level failures re-enable the card; 4xx responses keep the stamp. */
function isNetworkLevelFailure(err: unknown): boolean {
  if (err instanceof ChatHttpError) return err.status === 0 || err.status >= 500;
  // `fetch` itself rejected (network down, DNS failure, CORS, TLS) — surfaces
  // as TypeError in the browser.
  return err instanceof TypeError;
}

function isAbort(err: unknown): boolean {
  return (err instanceof DOMException || err instanceof Error) && err.name === "AbortError";
}

/** Apply a decision stamp to the approval on a specific message id. Extracted
 *  so the useCallback body doesn't nest a .map callback 4+ levels deep. */
function withApprovalDecision(
  messages: ChatMessage[],
  messageId: string,
  value: "approve" | "reject" | undefined,
): ChatMessage[] {
  return messages.map((m) =>
    m.id === messageId && m.approval ? { ...m, approval: { ...m.approval, decision: value } } : m,
  );
}

function completeApprovalSpan(span: CapabilitySpan, decision: "approve" | "reject"): void {
  const confirm = { confirmShown: true, confirmAccepted: decision === "approve" };
  if (decision === "approve") {
    span.complete({ outcome: "success", aiUsed: true, ...confirm });
    return;
  }
  const externalCount = span.facts.externalCount ?? 0;
  span.complete({
    outcome: externalCount > 0 ? "declined_external" : "cancelled",
    aiUsed: true,
    ...confirm,
  });
}

/** Drain the frontend tool calls the continuation queued — same batch
 *  semantics as useChat's resume loop (one /chat/resume per batch). */
async function drainPendingToolCalls(
  state: ReturnType<typeof createTurnState>,
  deps: TurnDeps,
  handleEvent: ReturnType<typeof createTurnEventHandler>,
  ctx: {
    threadIdRef: MutableRefObject<string | null>;
    host: Channel;
    apiToken: string | null;
    refresh?: RefreshTokenFn;
    signal: AbortSignal;
    resumeCtx: TurnContext;
  },
): Promise<void> {
  while (state.pendingQueue.length > 0) {
    const batch = state.pendingQueue.splice(0);
    let lastResult: Record<string, unknown> = {};
    for (const next of batch) {
      lastResult = await dispatchPendingToolCall(state, deps, next);
    }
    const lastCall = batch[batch.length - 1]!;
    await resumeChat(
      ctx.threadIdRef.current ?? "",
      lastCall.toolCallId,
      lastResult,
      ctx.host,
      ctx.apiToken,
      handleEvent,
      { refresh: ctx.refresh, signal: ctx.signal, ...ctx.resumeCtx },
    );
  }
}

/** Reconcile the UI with a failed resume. Abort keeps or drops the bubble
 *  depending on whether the user pressed Stop; a network-level failure rolls
 *  the optimistic stamp back; a 4xx keeps the stamp and shows an error. */
function handleApprovalResumeError(
  err: unknown,
  ctx: {
    assistantMsgId: string;
    turnTimestamp: number;
    userStoppedIdsRef: MutableRefObject<Set<string>>;
    setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
    stampDecision: (value: ("approve" | "reject") | undefined) => void;
  },
): void {
  const { assistantMsgId, turnTimestamp, userStoppedIdsRef, setMessages, stampDecision } = ctx;

  if (isAbort(err)) {
    if (userStoppedIdsRef.current.has(assistantMsgId)) {
      setMessages((prev) => prev.map((m) => (m.id === assistantMsgId ? { ...m, stopped: true } : m)));
    } else {
      setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
    }
    return;
  }

  if (isNetworkLevelFailure(err)) {
    setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
    stampDecision(undefined);
    return;
  }

  setMessages((prev) => {
    const updated = [...prev];
    const idx = updated.findIndex((m) => m.id === assistantMsgId);
    if (idx >= 0) {
      updated[idx] = {
        id: assistantMsgId,
        role: "assistant",
        content: "AiVA couldn't continue after your decision. Please try again.",
        timestamp: turnTimestamp,
      };
    }
    return updated;
  });
}

export function useApprovalDecision(args: UseApprovalDecisionArgs): ApprovalDecisionHandler {
  const {
    apiToken,
    host,
    threadIdRef,
    refresh,
    setMessages,
    setStreaming,
    nextMsgId,
    makeTurnDeps,
    trackAbort,
    userStoppedIdsRef,
    getResumeContext,
    onCapabilityRunEnded,
  } = args;

  // Interrupt ids with a resume currently in flight. Guards the double-click
  // window before the optimistic stamp lands in React state.
  const inFlightRef = useRef<Set<string>>(new Set());

  return useCallback(
    async (messageId: string, approval: ApprovalRequest, decision: "approve" | "reject") => {
      if (approval.decision !== undefined) return;
      if (inFlightRef.current.has(approval.interrupt_id)) return;
      if (host === null || !approval.interrupt_id) return;
      inFlightRef.current.add(approval.interrupt_id);

      const stampDecision = (value: ("approve" | "reject") | undefined) => {
        setMessages((prev) => withApprovalDecision(prev, messageId, value));
      };

      // Optimistic stamp — ApprovalCard disables its buttons once decision is set.
      stampDecision(decision);

      const assistantMsgId = nextMsgId();
      const turnTimestamp = Date.now();
      setMessages((prev) => [
        ...prev,
        { id: assistantMsgId, role: "assistant", content: "", timestamp: turnTimestamp },
      ]);
      setStreaming(true);

      const state = createTurnState();
      const deps = makeTurnDeps(assistantMsgId, turnTimestamp);
      const handleEvent = createTurnEventHandler(state, deps);
      const abort = new AbortController();
      const untrackAbort = trackAbort(abort);
      // Snapshot the client-owned context once for this decision's resume(s) so
      // the backend rebuilds the paused tool with the same message-id / draft /
      // search context the initial turn had (AIVA-5925).
      const resumeCtx = getResumeContext();

      try {
        await resumeChat(
          threadIdRef.current ?? "",
          approval.interrupt_id,
          {},
          host,
          apiToken,
          handleEvent,
          { kind: "approval", approval: { decision }, refresh, signal: abort.signal, ...resumeCtx },
        );

        await drainPendingToolCalls(state, deps, handleEvent, {
          threadIdRef,
          host,
          apiToken,
          refresh,
          signal: abort.signal,
          resumeCtx,
        });
      } catch (err) {
        handleApprovalResumeError(err, {
          assistantMsgId,
          turnTimestamp,
          userStoppedIdsRef,
          setMessages,
          stampDecision,
        });
      } finally {
        const span = deps.capabilitySpan;
        if (span) {
          completeApprovalSpan(span, decision);
          onCapabilityRunEnded?.();
        }
        untrackAbort();
        setStreaming(false);
        deps.setActivityLine(null);
        deps.setActiveTool(null);
        inFlightRef.current.delete(approval.interrupt_id);
      }
    },
    [apiToken, host, threadIdRef, refresh, setMessages, setStreaming, nextMsgId, makeTurnDeps, trackAbort, userStoppedIdsRef, getResumeContext, onCapabilityRunEnded],
  );
}
